import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Spinner from "react-bootstrap/Spinner";
import { setCookies } from "cookies-next";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { useAppDispatch } from "@/store";
import { fetchAuthUser } from "@/store/slices/auth";
import { fetchCartItems } from "@/store/slices/cart";
import { fetchWishlistItems } from "@/store/slices/wishlist";
import { initTryOnTracking } from "@/lib/tryOnTracking";
import { unbxdCusEngTrackEvent, unbxdCusEngTrackUser } from "@/lib/unbxd";
import moment from "moment-timezone";
import styles from "../../styles/shap_of_you.module.scss";
import { VerifyOtpScreenProps } from "./types";

const RESEND_OTP_INTERVAL_SECONDS = 30;

const VerifyOtpScreen: React.FC<VerifyOtpScreenProps> = ({
  navigateToStep,
  authData,
  onAuthComplete,
  logStep,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [resendTimeout, setResendTimeout] = useState(
    RESEND_OTP_INTERVAL_SECONDS
  );
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [verificationStatus, setVerificationStatus] = useState<
    "idle" | "verifying" | "verified" | "error"
  >("idle");
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const currentDate = moment().format("DD-MM-YYYY");

  const isOtpComplete = otpValues.every((val) => val !== "");

  // Countdown timer for resend OTP
  useEffect(() => {
    if (resendTimeout > 0) {
      const timer = setInterval(() => {
        setResendTimeout(resendTimeout - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendTimeout]);

  // OTP Formik
  const otpFormik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("OTP is required")
        .min(6, "Must be exactly 6 digits")
        .max(6, "Must be exactly 6 digits")
        .matches(/^\d{6}$/, "OTP must be 6 digits"),
    }),
    onSubmit: () => {}, // Not used anymore - using handleVerifyOtp and handleSubmit instead
  });

  // Step 1: Verify OTP (without navigating)
  const handleVerifyOtp = async () => {
    setVerificationStatus("verifying");
    setLoading(true);

    try {
      const otpString = otpValues.join("");

      if (authData.authFlow === "login") {
        // Login OTP verification
        const apiEndpoint = endpoints.auth.loginVerifyOtp();
        const requestPayload = {
          mobile: authData.mobileNumber,
          otp: otpString,
          otp_uuid: authData.otpUid,
        };

        const { data } = await httpClient.post(apiEndpoint, requestPayload);

        // Log successful login (non-blocking)
        if (logStep) {
          logStep("signup_login_success", {
            api_endpoint: apiEndpoint,
            request_payload: { ...requestPayload, otp: "******" }, // Sanitized
            response_status: 200,
            response_payload: { access_token: "[token]" }, // Sanitized
            success_message: "Login successful",
          });

          // Also log generic login/register success
          logStep("signup_login_success", {
            api_endpoint: apiEndpoint,
            response_status: 200,
            success_message: "Login successful",
          });
        }

        // Store access token and set verified status
        setAccessToken(data?.access_token);
        setVerificationStatus("verified");

        // Automatically proceed to login after successful verification
        await handleAutoLogin(data?.access_token);
      } else {
        // Register OTP verification
        const apiEndpoint = endpoints.auth.signup();
        const requestPayload = {
          name: `${authData.registerData.firstName} ${authData.registerData.lastName}`,
          mobile: authData.mobileNumber,
          email: authData.registerData.email,
          pincode: authData.registerData.pincode,
          otp: otpString,
          otp_uuid: authData.otpUid,
          source: "shape_of_you",
        };

        const { data } = await httpClient.post(apiEndpoint, requestPayload);

        // Log successful registration (non-blocking)
        if (logStep) {
          logStep("signup_register_success", {
            api_endpoint: apiEndpoint,
            request_payload: { ...requestPayload, otp: "******" }, // Sanitized
            response_status: 200,
            response_payload: { access_token: "[token]" }, // Sanitized
            success_message: "Registration successful",
          });

          // Also log generic login/register success
          logStep("signup_register_success", {
            api_endpoint: apiEndpoint,
            response_status: 200,
            success_message: "Registration successful",
          });
        }

        // Track registration events
        if (window.Moengage) {
          window.Moengage.track_event("Registration Completed", {
            mobile: authData.mobileNumber,
            platform: "website",
          });
        }

        if (window.smartech) {
          unbxdCusEngTrackUser({
            "pk^mobile": authData.mobileNumber,
            NAME: `${authData.registerData.firstName} ${authData.registerData.lastName}`,
            email: authData.registerData.email,
            PINCODE: authData.registerData.pincode,
            SENCO_USER: "yes",
            REGISTRATION_DATE_SENCO: currentDate,
          });
          window.smartech("identify", authData.mobileNumber);
          setTimeout(() => {
            unbxdCusEngTrackEvent("Registration", {
              source: "web",
              platform: "senco",
            });
          }, 200);
        }

        // Store access token and set verified status
        setAccessToken(data.access_token);
        setVerificationStatus("verified");

        // Automatically proceed to login after successful verification
        await handleAutoLogin(data.access_token);
      }
    } catch (error: any) {
      // Log API error (non-blocking)
      if (logStep) {
        const stepName =
          authData.authFlow === "login"
            ? "signup_login_success"
            : "signup_register_success";
        logStep(stepName, {
          api_endpoint:
            authData.authFlow === "login"
              ? endpoints.auth.loginVerifyOtp()
              : endpoints.auth.signup(),
          response_status: error.response?.status || 500,
          error_message:
            error.response?.data?.message || "OTP verification failed",
          error_code: error.response?.status?.toString() || "500",
        });
      }

      // Check if error is "OTP does not match"
      if (error.response?.status === 400) {
        setVerificationStatus("error");
      } else {
        setVerificationStatus("error");
        if (error.response?.data?.message) {
          toast(error.response.data.message, {
            type: "error",
            toastId: "auth-otp-error",
          });
        } else {
          toast("Something went wrong! Try again!", {
            type: "error",
            toastId: "auth-otp-error",
          });
        }
      }
    }
    setLoading(false);
  };

  // Auto-login after successful registration/login
  const handleAutoLogin = async (token: string) => {
    try {
      setCookies("token", token, { path: "/" });
      const { payload: user }: any = await dispatch(fetchAuthUser());

      toast(`Welcome ${user.name}`, {
        type: "success",
        toastId: "auth-login-success",
      });

      if (window.Moengage) {
        window.Moengage.track_event("Login", {
          mobile: user.mobile,
        });
        window.Moengage.add_first_name(user.name);
        window.Moengage.add_mobile(user.mobile);
        window.Moengage.add_unique_user_id(user.uuid);
        if (user.email) {
          window.Moengage.add_email(user.email);
        }
      }

      if (window.smartech) {
        unbxdCusEngTrackUser({
          "pk^mobile": user?.mobile,
          SENCO_USER: "yes",
        });
        window.smartech("identify", user?.mobile);
        unbxdCusEngTrackEvent("Sign in ", {
          source: "web",
          platform: "senco",
        });
      }

      dispatch(fetchCartItems());
      dispatch(fetchWishlistItems(user.id));
      initTryOnTracking(user.id);

      // Check if there's a pending consent acceptance from T&C screen
      const pendingConsent = localStorage.getItem("pendingConsentAcceptance");
      if (pendingConsent === "true") {
        try {
          // Call updateConsentForm API
          await httpClient.post(endpoints.auth.updateConsentForm(), {
            status: "accept",
          });

          // Clear the localStorage flag
          localStorage.removeItem("pendingConsentAcceptance");

          console.log("Pending consent accepted successfully");
        } catch (consentError) {
          console.error("Error updating pending consent:", consentError);
          // Don't block the login flow if consent update fails
        }
      }

      // Call parent completion handler or fallback to navigation
      if (onAuthComplete) {
        onAuthComplete();
      } else {
        // Fallback: Navigate to product view after successful login
        navigateToStep("productView");
      }
    } catch (error: any) {
      console.error("Auto-login error:", error);
      toast("Login successful but failed to fetch user data", {
        type: "warning",
        toastId: "auth-warning",
      });
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtpValues = [...otpValues];
      newOtpValues[index] = value;
      setOtpValues(newOtpValues);

      // Reset verification status when user modifies OTP
      if (verificationStatus === "error" || verificationStatus === "verified") {
        setVerificationStatus("idle");
      }

      // Update formik value
      const otpString = newOtpValues.join("");
      otpFormik.setFieldValue("otp", otpString, false);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendTimeout > 0) return;

    setSendOtpLoading(true);
    try {
      let apiEndpoint: string;
      let requestPayload: any;

      if (authData.authFlow === "login") {
        apiEndpoint = endpoints.auth.loginSendOtp();
        requestPayload = {
          mobile: authData.mobileNumber,
        };
      } else {
        // Register flow - use the same endpoint as SignupScreen
        apiEndpoint = endpoints.auth.sendOtp();
        requestPayload = {
          mobile: authData.mobileNumber,
          email: authData.registerData.email,
          pincode: authData.registerData.pincode,
        };
      }

      const { data } = await httpClient.post(apiEndpoint, requestPayload);

      toast(data.message || "OTP resent successfully", {
        type: "success",
        toastId: "auth-otp-resend-success",
      });

      // Reset timer
      setResendTimeout(RESEND_OTP_INTERVAL_SECONDS);

      // Note: We don't update authData.otpUid here as it's passed as prop
      // If needed, parent component should handle updating the otpUid
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast(error.response.data.message, {
          type: "error",
          toastId: "auth-otp-resend-error",
        });
      } else {
        toast("Failed to resend OTP. Please try again!", {
          type: "error",
          toastId: "auth-otp-resend-error",
        });
      }
    }
    setSendOtpLoading(false);
  };

  return (
    <div className="d-md-none w-100">
      <div className={`${styles.wrappersignup} ${styles.wrappersignup_otp}`}>
        <div className={styles.max_h}>
          {/* Header */}
          <h1
            style={{
              color: "#d61f26",
              fontSize: "40px",
              marginBottom: "6px",
              fontFamily: "daisihill",
            }}
          >
            Verify OTP
          </h1>
          <p
            style={{
              color: "#444",
              fontSize: "14px",
              marginBottom: "40px",
            }}
          >
            OTP has been sent to your phone number
          </p>

          {/* OTP Input Boxes (6-digit) */}
          <form onSubmit={otpFormik.handleSubmit}>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                marginBottom: "40px",
              }}
            >
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="number"
                  maxLength={1}
                  value={otpValues[index]}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  style={{
                    width: "48px",
                    height: "56px",
                    border:
                      verificationStatus === "error"
                        ? "2px solid #dc3545"
                        : // : verificationStatus === "verified"
                          // ? "2px solid #28a745"
                          "1px solid #a09c9c",
                    backgroundColor:
                      verificationStatus === "error"
                        ? "#ffe6e6"
                        : // : verificationStatus === "verified"
                          // ? "#e6f7e6"
                          "white",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontSize: "22px",
                    fontWeight: "600",
                    color: "#262121",
                    flex: "0 0 auto",
                  }}
                />
              ))}
            </div>

            {/* Error message for incorrect OTP */}
            {verificationStatus === "error" && (
              <small
                className="text-danger"
                style={{
                  display: "block",
                  marginTop: "-30px",
                  marginBottom: "10px",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#dc3545",
                }}
              >
                Error! OTP incorrect
              </small>
            )}

            {otpFormik.touched.otp && otpFormik.errors.otp ? (
              <small
                className="text-danger"
                style={{
                  display: "block",
                  marginTop: "10px",
                  marginBottom: "10px",
                  textAlign: "center",
                }}
              >
                {otpFormik.errors.otp}
              </small>
            ) : null}

            {/* Resend OTP Section - After OTP input */}
            <div
              style={{
                marginTop: "20px",
                fontSize: "14px",
                fontFamily: "Sweet Sans Pro Regular",
              }}
            >
              {/* OTP Timer - Always visible, disabled when over */}
              <div
                style={{
                  marginBottom: "5px",
                  fontSize: "12px",
                  color: resendTimeout > 0 ? "#666" : "#ccc",
                  fontFamily: "Sweet Sans Pro Regular",
                }}
              >
                (OTP Timer {resendTimeout > 0 ? `${resendTimeout}s` : "over"})
              </div>

              {/* Didn't get an OTP - Always disabled */}
              <span style={{ color: "#999", marginRight: "5px" }}>
                Didn&apos;t get an OTP?
              </span>

              {/* Resend OTP - Disabled when timer running, blue when enabled */}
              {resendTimeout > 0 ? (
                <span style={{ color: "#ccc", cursor: "not-allowed" }}>
                  Resend OTP
                </span>
              ) : (
                <button
                  onClick={handleResendOtp}
                  disabled={sendOtpLoading}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#007bff",
                    fontSize: "14px",
                    textDecoration: "underline",
                    cursor: sendOtpLoading ? "not-allowed" : "pointer",
                    fontFamily: "Sweet Sans Pro Medium",
                    padding: 0,
                  }}
                >
                  Resend OTP{" "}
                  {sendOtpLoading && <Spinner animation="border" size="sm" />}
                </button>
              )}
            </div>
          </form>
        </div>
        <div className={styles.btn_nn}>
          {/* Edit Phone Number Link */}
          <button
            style={{
              background: "transparent",
              border: "none",
              color: "#262121",
              fontSize: "20px",
              textDecoration: "none",
              cursor: "pointer",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              margin: "0 auto 20px",
              fontFamily: "Sweet Sans Pro Medium",
            }}
            onClick={() => navigateToStep("login")}
          >
            Edit Phone Number{" "}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.4249 4.60509L17.4149 3.6151C18.2351 2.79497 19.5648 2.79497 20.3849 3.6151C21.205 4.43524 21.205 5.76493 20.3849 6.58507L19.3949 7.57506M16.4249 4.60509L9.76558 11.2644C9.25807 11.772 8.89804 12.4078 8.72397 13.1041L8 16L10.8959 15.276C11.5922 15.102 12.228 14.7419 12.7356 14.2344L19.3949 7.57506M16.4249 4.60509L19.3949 7.57506"
                stroke="#262121"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M18.9999 13.5C18.9999 16.7875 18.9999 18.4312 18.092 19.5376C17.9258 19.7401 17.7401 19.9258 17.5375 20.092C16.4312 21 14.7874 21 11.4999 21H11C7.22876 21 5.34316 21 4.17159 19.8284C3.00003 18.6569 3 16.7712 3 13V12.5C3 9.21252 3 7.56879 3.90794 6.46244C4.07417 6.2599 4.2599 6.07417 4.46244 5.90794C5.56879 5 7.21252 5 10.5 5"
                stroke="#262121"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Verify/Submit Button */}
          <button
            className={styles.btn}
            onClick={(e) => {
              e.preventDefault();
              if (isOtpComplete && !loading) {
                handleVerifyOtp();
              }
            }}
            disabled={loading || !isOtpComplete}
            style={
              loading || !isOtpComplete
                ? {
                    marginTop: "20px",
                    background: "#d3d3d3",
                    width: "100%",
                    display: "block",
                    color: "#888",
                    fontSize: "20px",
                    fontFamily: "Sweet Sans Pro Medium",
                    cursor: "not-allowed",
                    opacity: 0.6,
                  }
                : {
                    marginTop: "20px",
                    background: "#C9302C",
                    width: "100%",
                    display: "block",
                    color: "white",
                    fontSize: "20px",
                    fontFamily: "Sweet Sans Pro Medium",
                    cursor: "pointer",
                  }
            }
          >
            {loading ? "Submitting..." : "Submit"} <span>→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtpScreen;
